/** Format an integer pesewa amount as Ghana cedis, e.g. 25000 → "GHS 250.00".
 * Money is stored as integer pesewas everywhere (CLAUDE.md); the UI divides by
 * 100 only at the display boundary. */
export function formatPesewas(pesewas: number): string {
  const cedis = pesewas / 100
  const amount = cedis.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
  return `GHS ${amount}`
}

/** What a price cell says, and which of ADR-0054's four readings produced it. */
export type PriceReading = {
  /** What goes where a price goes. */
  text: string
  /**
   * `amount` and `minimum` are figures. `varies` and `uncollected` are both
   * absences of one and READ THE SAME to the patient — they share an action —
   * but they stay distinct here because the REASON differs and the detail line
   * says which (ADR-0052's rule, kept alive by ADR-0054).
   */
  kind: 'amount' | 'minimum' | 'varies' | 'uncollected'
  /**
   * The top-up statement. Always present, never the same weight.
   *
   * Every price is provisional (ADR-0054): the hospital may find more is needed
   * and charge the difference. The owner's example is the one to keep in mind —
   * a patient booked for an MRI may be asked to do a kidney function test
   * first, which changes the total.
   */
  caveat: string
}

/**
 * Words for a price, or an honest account of why there is not one.
 *
 * FOUR READINGS, AND THE LAST TWO SHARE AN ACTION (ADR-0054):
 *
 *   a figure                  -> "GHS 120.00"
 *   a floor                   -> "From GHS 120.00"
 *   varies, no floor given    -> AMTC will call to confirm a price
 *   nothing collected yet     -> AMTC will call to confirm a price
 *
 * The last two were a dead end until M28: an unpriced procedure was REFUSED for
 * booking, so a hospital slow with its price list removed procedures from the
 * product for every patient. Now they are bookable and AMTC confirms the figure
 * with the hospital by phone.
 *
 * "CONFIRM A PRICE FROM THE HOSPITAL", NEVER "AGREE A PRICE" — owner correction,
 * 2026-09-15. "Agree" reads as an invitation to negotiate; the negotiation, such
 * as it is, happens between AMTC and the hospital. The patient is being told
 * what something costs, not asked to haggle.
 *
 * THE CAVEAT IS SPECIFIC ON PURPOSE. "Prices may vary" is a get-out clause: it
 * protects us and tells the patient nothing. Naming what can change — the
 * hospital finding that more is needed — is information they can act on.
 */
export function readPrice(
  pricePesewas: number | null | undefined,
  opts: { varies: boolean; isMinimum?: boolean } | boolean = false,
): PriceReading {
  // The boolean form is the old two-argument signature, kept so a caller that
  // only knows about `price_varies` still compiles and behaves.
  const { varies, isMinimum } =
    typeof opts === 'boolean' ? { varies: opts, isMinimum: false } : opts

  if (pricePesewas != null) {
    if (isMinimum) {
      return {
        text: `From ${formatPesewas(pricePesewas)}`,
        kind: 'minimum',
        caveat:
          'This is the least the hospital charges. The final amount depends on your case and is confirmed at the hospital.',
      }
    }
    return {
      text: formatPesewas(pricePesewas),
      kind: 'amount',
      caveat:
        'This is the hospital’s price for the procedure itself. If they find you need additional tests, those are charged separately.',
    }
  }

  return {
    text: 'AMTC will call to confirm a price from the hospital',
    kind: varies ? 'varies' : 'uncollected',
    caveat: varies
      ? 'This procedure is not charged at a fixed rate, so AMTC agrees the figure with the hospital before you pay.'
      : 'The hospital has not sent us this price yet, so AMTC confirms it with them before you pay.',
  }
}
